import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import InfoIcon from '@mui/icons-material/Info'
import {
  Box,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Modal,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import PackageAPI from 'api/faber/PackageAPI'
import UsaAPI from 'api/faber/UsaAPI'
import NormalText from 'common/Typography/NormalText'
import { format, fromUnixTime } from 'date-fns'
import OrbitSession from 'hooks/useCookies'
import {
  GetActiveUserPlansDetailsType,
  GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
} from 'types/api/faber/oneTimePack.type'

import ReassignPackageDialog from '../dialogs/ReassignPackageDialog'

interface Props {
  activeOtpackUserPlans?: GetActiveUserPlansDetailsType[]
  allInactiveUserPlans?: GetAllInactiveAndTerminatedOtpackUserPlansDetailsType[]
  setOtpackDataRemaining: (value: number) => void
  isExternal: boolean
  userId: string
}
export default function CustomerPlansCard({
  allInactiveUserPlans,
  activeOtpackUserPlans,
  setOtpackDataRemaining,
  isExternal,
  userId
}: Props) {
  const faberToken: string = OrbitSession.get('faberToken')

  const navigate = useNavigate()

  const [totalActiveDataLeft, setTotalActiveDataLeft] = useState(0)
  const [totalInactiveData, setTotalInactiveData] = useState(0)
  const [selectedItem, setSelectedItem] = useState<
    | GetActiveUserPlansDetailsType
    | GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
    | undefined
  >(undefined)
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false)
  const [showMoreResetPackageInfoModal, setShowMoreResetPackageInfoModal] = useState(false)
  const [showMoreTerminatePackageInfoModal, setShowMoreTerminatePackageInfoModal] = useState(false)
  const [showReassignDialog, setShowReassignDialog] = useState(false); 

  const { data: packageDetails } = useQuery(
    ['getPackageDetails', selectedItem?.appliedPlanId ?? ''],
    async () =>
    (isExternal ?
      PackageAPI.getExternalPackageDetails(
        selectedItem?.appliedPlanId ?? '',
        faberToken
      ) :
      PackageAPI.getPackageDetails(
        selectedItem?.appliedPlanId ?? '',
        faberToken
      )),
    { enabled: !!selectedItem }
  )
  const packageData = packageDetails?.data.data

  const [selectedPackTotalData, setSelectedPackTotalData] = useState(0)
  const [selectedPackRemainingData, setSelectedPackRemainingData] = useState(0)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openMenuId, setOpenMenuId] = useState(null);

  // If more than 100MB, they are not allowed to reset the pack
  // const overResetLimit: boolean = (selectedPackUsedData / (1024 * 1024 * 1024) > 0.1) ? true : false
  let overResetLimit: boolean
  if (selectedPackTotalData - (selectedPackRemainingData / (1024 * 1024 * 1024)) > 0.1) {
    overResetLimit = true
  } else {
    overResetLimit = false
  }

  const resetPackage = () => {
    if (packageData === undefined || packageData.packageId === undefined) {
      toast.error("Unable to replace package as packageId is undefined. If the values are empty, give it a moment to let it load")
      return
    }

    // Depending on whether this is a external user page, use the right replace package api
    (isExternal ?
      PackageAPI.externalReplacePackage(
        packageData.packageId,
        faberToken)
      :
      PackageAPI.replacePackage(
        packageData.packageId,
        faberToken))
      .then(() => {
        toast.success("Successfully replaced package, please refresh the page to see the new updated PackageID")
        return
      }).catch(() => {
        toast.error("Failed and did not replace the package, please contact the engineers")
        return
      })

    return
  }

  const terminatePackage = () => {
    if (packageData === undefined || packageData.packageId === undefined) {
      toast.error("Unable to terminate package as packageId is undefined. If the values are empty, give it a moment to let it load")
      return
    }

    // Depending on whether this is a external user page, use the right replace package api
    (isExternal ?
      PackageAPI.externalTerminatePackage(
        packageData.packageId,
        faberToken)
      :
      PackageAPI.terminatePackage(
        packageData.packageId,
        faberToken))
      .then(() => {
        toast.success(`Successfully terminated package, please refresh the page to verify the package ID ${packageData.packageId} is terminated`)
        return
      }).catch(() => {
        toast.error("Failed and was not able to terminate the package, please contact the engineers")
        return
      })

    return
  }


  const getUsers = async (user_id: string) => {
      if (user_id.includes('|')) {
        // Global users
        const response = await UsaAPI.getAllGlobalUsers({
          searchCriteria: 'User ID',
          searchValue: user_id,
          faberToken: faberToken
        })
        if (response.status === 200) {
          // Mapping present in FaberGlobalUserSearch.tsx
          return response.data.data.users.map(
            ({ name, family_name, given_name, user_id, user_metadata }) => ({
              name,
              family_name,
              given_name,
              user_id,
              user_metadata,
              login_method: user_id
            })
          )
        }
      } else {
        const response = await OneTimePackApi.getAllFaberUsers({
          searchCriteria: 'Customer ID',
          searchValue: user_id,
          faberToken: faberToken,
          limit: 10,
          planStatus: 'active',
          offset: 0
        })
        if (response.status === 200) {
          // Mapping present in RevampedFaberCustomerDashboard.tsx
          return response.data.body.data.map(
            ({
              _id,
              user_id,
              user_metadata,
              email,
              contactNo,
              address,
              createdAt,
              firstName,
              lastName,
              role,
              showPlanPage,
              showTutorial,
              sin,
              status,
              updatedAt,
              subscription,
              otpack
            }) => ({
              _id,
              user_id,
              user_metadata,
              contactNo,
              firstName,
              email,
              msisdn: contactNo.number,
              address,
              createdAt,
              lastName,
              role,
              showPlanPage,
              showTutorial,
              sin,
              status,
              updatedAt,
              subscription,
              otpack
            })
          )
        }
      }
      return []
    }
  
  const handleViewUser = async (user_id: string) => { 
      const userId = user_id
      const userInfo = await getUsers(user_id)
      if (userInfo.length == 1) {
        navigate(`/hq/manage-global-cx/view-profile/${userId}`, { state: userInfo[0] })
      } else {
        toast.error('Error fetching promocode user profile', {
          toastId: 'error-get-promocode-user-profile'
        })
      }
    }
  
  const handleClick = (event: React.MouseEvent<HTMLElement>, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenuId(item.appliedPlanId); // Use unique identifier for each item
  };

  const handleMenuClose = () => {
    setAnchorEl(null); 
    setOpenMenuId(null);
  };

  const handleReassignPackage = (item) => {
    setSelectedItem(item);
    setShowReassignDialog(true);
    handleMenuClose()
  }

  const handleResetPackageActive = (item) => {
    setSelectedItem(item)
    setShowMoreResetPackageInfoModal(true)
    setSelectedPackTotalData(item.packageDataAllowed)
    setSelectedPackRemainingData(item.dataUsageRemainingInBytes)
    handleMenuClose()
  }

  const handleTerminatePackageActive = (item) => {
    setSelectedItem(item)
    setShowMoreTerminatePackageInfoModal(true)
    setSelectedPackTotalData(item.packageDataAllowed)
    setSelectedPackRemainingData(item.dataUsageRemainingInBytes)
    handleMenuClose()
  }

  const handleResetPackageInactive = (item) => {
    setSelectedItem(item)
    setShowMoreResetPackageInfoModal(true)
    setSelectedPackRemainingData(item.packageDataAllowed * 1024 * 1024 * 1024) // remaining data to be in bytes
    setSelectedPackTotalData(item.packageDataAllowed)
    handleMenuClose()
  }

  const handleTerminatePackageInactive = (item) => {
    setSelectedItem(item)
    setShowMoreTerminatePackageInfoModal(true)
    setSelectedPackRemainingData(item.packageDataAllowed * 1024 * 1024 * 1024) // remaining data to be in bytes
    setSelectedPackTotalData(item.packageDataAllowed)
  }

  // This is active/ianctive packs
  const getActiveOtpUserPacks = useMemo(() => {
    if (activeOtpackUserPlans) {
      const getAllPlans = activeOtpackUserPlans.map((item) => {
        const isMenuOpen = openMenuId === item.appliedPlanId;

        return (
          <Grid
            key={`active-${item.createdAt}+`}
            container
            sx={{
              justifyContent: 'space-between'
            }}
          >
            <Grid item xs={2}>
              <Stack flexDirection={'column'} sx={{ alignItems: 'start', padding: 0, margin: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center', 
                    padding: 0, margin: 0,  
                  }}
                >
                  <NormalText sx={{ padding: 0, margin: 0}}>{item.packageName}</NormalText>
                  <Tooltip title={''}>
                    <InfoIcon
                      onClick={() => {
                        setSelectedItem(item)
                        setShowMoreInfoModal(true)
                      }}
                    style={{ color: 'lightgrey', fontSize: '18' }}
                    />
                  </Tooltip>
                </Box>
                {item.allocatedFrom  && (
                  <div onClick={() => handleViewUser(item.allocatedFromID)}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        padding: 0,
                        margin: 0,
                        marginBottom: 1,
                        color: 'blue',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >from: {item.allocatedFrom ?? "N/A"}</Typography> 
                  </div>
                )}
              </Stack>
            </Grid>
            <Grid item xs={2}>
              <Stack flexDirection={'row'} sx={{ alignItems: 'center' }}>
                <Box sx={{ width: '40%', px: 1 }}>
                  <LinearProgress
                    color='primary'
                    variant='determinate'
                    value={
                      ((item.packageDataAllowed -
                        item.dataUsageRemainingInBytes / 1024 * 1024 * 1024) /
                        item.packageDataAllowed) *
                      100
                    }
                  />
                </Box>
                <Box sx={{ minWidth: '20%' }}>
                  <NormalText variant='body3' sx={{ alignItems: 'center' }}>
                    {parseFloat(
                      (
                        item.packageDataAllowed -
                        item.dataUsageRemainingInBytes / 1073741824
                      ).toFixed(2)
                    )}
                    /{item.packageDataAllowed} GB
                  </NormalText>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={2}>
              <NormalText>
                {format(fromUnixTime(item.createdAt / 1000), 'dd MMM yyyy HH:mm')}
              </NormalText>
            </Grid>
            <Grid item xs={2}>
              {' '}
              <NormalText>{item.planStatus}</NormalText>
            </Grid>
            <Grid item xs={1}>
              <NormalText>{item.packageValidityInDays} days</NormalText>
            </Grid>
            <Grid item xs={1}>
              <IconButton
                onClick={(event) => handleClick(event, item)}
              >
                <MoreVertIcon />
              </IconButton>
             <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { minWidth: 150 }
                }}
              >
                <MenuItem onClick={()=>handleReassignPackage(item)}>
                  Reassign Package
                </MenuItem>
                <MenuItem onClick={()=>handleResetPackageActive(item)}> 
                  Reset Package
                </MenuItem>
                <MenuItem onClick={()=>handleTerminatePackageActive(item)}> 
                  Terminate Package
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
        )
      }
      )
      return getAllPlans
    }
  }, [activeOtpackUserPlans, anchorEl, openMenuId])

  const getAllInactiveAndTerminatedOtpackUserPlans = useMemo(() => {
    if (allInactiveUserPlans) {
      const getAllPlans = allInactiveUserPlans.map((item) => {  
        const isMenuOpen = openMenuId === item.appliedPlanId;

        return (
          <Grid
            key={`inactive-terminated-${item.createdAt}+`}
            container
            sx={{
              justifyContent: 'space-between'
            }}
          >
            <Grid item xs={2}>
              <Stack flexDirection={'column'} sx={{ alignItems: 'start', padding: 0, margin: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center', 
                    padding: 0, margin: 0,  
                  }}
                >
                  <NormalText sx={{ padding: 0, margin: 0}}>{item.packageName}</NormalText>
                  <Tooltip title={''}>
                    <InfoIcon
                      onClick={() => {
                        setSelectedItem(item)
                        setShowMoreInfoModal(true)
                      }}
                    style={{ color: 'lightgrey', fontSize: '18' }}
                    />
                  </Tooltip>
                </Box>
                {item.allocatedFrom && (
                  <div onClick={() => handleViewUser(item.allocatedFromID)}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        padding: 0,
                        margin: 0,
                        marginBottom: 1,
                        color: 'blue',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >from: {item.allocatedFrom ?? "N/A"}</Typography> 
                  </div>
                )}
              </Stack>
            </Grid>
            <Grid item xs={2}>
              <Stack flexDirection={'row'} sx={{ alignItems: 'center' }}>
                <Box sx={{ width: '40%', px: 1 }}>
                  <LinearProgress
                    color='primary'
                    variant='determinate'
                    value={0}
                  />
                </Box>
                <Box sx={{ minWidth: '20%' }}>
                  <NormalText variant='body3' sx={{ alignItems: 'center' }}>
                    0/{item.packageDataAllowed} GB
                  </NormalText>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={2}>
              <NormalText theme='muted'>
                {format(
                  fromUnixTime(item.createdAt / 1000),
                  'dd MMM yyyy HH:mm'
                )}
              </NormalText>
            </Grid>
            <Grid item xs={2}>
              <NormalText theme='muted'>{item.planStatus}</NormalText>
            </Grid>
            <Grid item xs={1}>
              <NormalText theme='muted'>
                {item.packageValidityInDays} days
              </NormalText>
            </Grid>
            <Grid item xs={1}>
              <IconButton
                onClick={(event) => handleClick(event, item)}
              >
                <MoreVertIcon />
              </IconButton>
             <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { minWidth: 150 }
                }}
              >
                <MenuItem onClick={()=>handleReassignPackage(item)}>
                  Reassign Package
                </MenuItem>
                <MenuItem onClick={()=>handleResetPackageInactive(item)}> 
                  Reset Package
                </MenuItem>
                <MenuItem onClick={()=>{handleTerminatePackageInactive(item), handleMenuClose()}}> 
                  Terminate Package
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
        )
      })
      return getAllPlans
    }
  }, [allInactiveUserPlans, anchorEl, openMenuId])

  useEffect(() => {
    if (activeOtpackUserPlans) {
      const activeTotalDataLeft = activeOtpackUserPlans.reduce(
        (accumulator, item) =>
          accumulator + item.dataUsageRemainingInBytes / 1073741824,
        0
      )
      setTotalActiveDataLeft(parseFloat(activeTotalDataLeft.toFixed(2)))
    }
    if (allInactiveUserPlans) {
      const inactiveData = allInactiveUserPlans.reduce(
        (accumulator, item) => accumulator + item.packageDataAllowed,
        0
      )
      setTotalInactiveData(inactiveData)
    }
  }, [allInactiveUserPlans, activeOtpackUserPlans])

  useEffect(() => {
    setOtpackDataRemaining(totalActiveDataLeft + totalInactiveData)
  }, [totalActiveDataLeft, totalInactiveData])

  return (
    <>
      <Stack>
        {/* The information pop up */}
        <Modal
          open={showMoreInfoModal}
          onClose={() => setShowMoreInfoModal(false)}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Box sx={modalStyle}>
            <Typography variant='h4' gutterBottom>
              Package Details
            </Typography>
            <NormalText>Package ID: {packageData?.packageId}</NormalText>
            <NormalText>
              Package Type ID: {packageData?.packageTypeId}
            </NormalText>
            <NormalText>Status: {packageData?.status}</NormalText>
            <NormalText>
              Data Remaining:{' '}
              {packageData?.dataUsageRemainingInBytes
                ? packageData.dataUsageRemainingInBytes / (1024 * 1024 * 1024) +
                ' GB'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Created:{' '}
              {packageData?.dateCreated !== undefined
                ? packageData?.dateCreated !== 0
                  ? format(
                    new Date(packageData.dateCreated),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Expiry:{' '}
              {packageData?.dateExpiry !== undefined
                ? packageData?.dateExpiry !== 0
                  ? format(
                    new Date(packageData.dateExpiry),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Activated:{' '}
              {packageData?.dateActivated !== undefined
                ? packageData?.dateActivated !== 0
                  ? format(
                    new Date(packageData.dateActivated),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Terminated:{' '}
              {packageData?.dateTerminated !== undefined
                ? packageData?.dateTerminated !== 0
                  ? format(
                    new Date(packageData.dateTerminated),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
          </Box>
        </Modal>

        {/* The reset information pop up */}
        <Modal
          open={showMoreResetPackageInfoModal}
          onClose={() => setShowMoreResetPackageInfoModal(false)}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Box sx={modalStyle}>
            <Typography variant='h4' gutterBottom>
              Do you really want to reset the package?
            </Typography>
            <NormalText>Package ID: {packageData?.packageId}</NormalText>
            <NormalText>
              Package Type ID: {packageData?.packageTypeId}
            </NormalText>
            <NormalText>Status: {packageData?.status}</NormalText>

            <NormalText sx={{ color: overResetLimit ? 'red' : 'green' }}>
              {/* Data Remaining:{' '} */}
              {/* {packageData?.dataUsageRemainingInBytes
                ? packageData.dataUsageRemainingInBytes / (1024 * 1024 * 1024) +
                  ' GB'
                : 'N/A'} */}
              {/* Data Used:{' '} */}
              {/* {packageData?.dataUsageRemainingInBytes
                ? 
                (100 - packageData?.dataUsageRemainingInBytes / (1024 * 1024 * 1024) + ' GB')
                : 'N/A'} */}
              Data Used:{' '}
              {
                // parseFloat((selectedPackUsedData / (1024 * 1024 * 1024)).toFixed(2))
                (selectedPackTotalData - (selectedPackRemainingData / (1024 * 1024 * 1024))).toFixed(2)
                + ' GB'
              }
            </NormalText>
            <NormalText>
              Date Created:{' '}
              {packageData?.dateCreated !== undefined
                ? packageData?.dateCreated !== 0
                  ? format(
                    new Date(packageData.dateCreated),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Expiry:{' '}
              {packageData?.dateExpiry !== undefined
                ? packageData?.dateExpiry !== 0
                  ? format(
                    new Date(packageData.dateExpiry),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Activated:{' '}
              {packageData?.dateActivated !== undefined
                ? packageData?.dateActivated !== 0
                  ? format(
                    new Date(packageData.dateActivated),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Terminated:{' '}
              {packageData?.dateTerminated !== undefined
                ? packageData?.dateTerminated !== 0
                  ? format(
                    new Date(packageData.dateTerminated),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>

            <Button
              variant="contained"
              disabled={overResetLimit} // Only allow the button if it is not over the limit
              onClick={() => {
                setShowMoreResetPackageInfoModal(false)
                resetPackage()
              }}>Yes</Button>
            <Button onClick={() => { setShowMoreResetPackageInfoModal(false) }}>No</Button>
          </Box>
        </Modal>

        {/* The terminate information pop up */}
        <Modal
          open={showMoreTerminatePackageInfoModal}
          onClose={() => setShowMoreTerminatePackageInfoModal(false)}
          aria-labelledby='modal-modal-title'
          aria-describedby='modal-modal-description'
        >
          <Box sx={modalStyle}>
            <Typography variant='h4' gutterBottom color="red">
              Do you really want to terminate the package?
            </Typography>

            <Typography variant='h6' gutterBottom color="red">
              This will terminate the pack immediately and cannot be undone. Please proceed with caution
            </Typography>

            <NormalText>Package ID: {packageData?.packageId}</NormalText>
            <NormalText>
              Package Type ID: {packageData?.packageTypeId}
            </NormalText>
            <NormalText>Status: {packageData?.status}</NormalText>

            {/* No color formatting needed because this is not replacing a package */}
            <NormalText>
              Data Used:{' '}
              {
                (selectedPackTotalData - (selectedPackRemainingData / (1024 * 1024 * 1024))).toFixed(2)
                + ' GB'
              }
            </NormalText>
            <NormalText>
              Date Created:{' '}
              {packageData?.dateCreated !== undefined
                ? packageData?.dateCreated !== 0
                  ? format(
                    new Date(packageData.dateCreated),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Expiry:{' '}
              {packageData?.dateExpiry !== undefined
                ? packageData?.dateExpiry !== 0
                  ? format(
                    new Date(packageData.dateExpiry),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Activated:{' '}
              {packageData?.dateActivated !== undefined
                ? packageData?.dateActivated !== 0
                  ? format(
                    new Date(packageData.dateActivated),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>
            <NormalText>
              Date Terminated:{' '}
              {packageData?.dateTerminated !== undefined
                ? packageData?.dateTerminated !== 0
                  ? format(
                    new Date(packageData.dateTerminated),
                    'dd MMM yyyy HH:mm'
                  ) + ' GMT+8'
                  : 'N/A'
                : 'N/A'}
            </NormalText>

            <Button
              sx={{
                color: "white",
                background: "red",
              }}
              variant="contained"
              onClick={() => {
                setShowMoreTerminatePackageInfoModal(false)
                terminatePackage()
              }}>Yes</Button>
            <Button onClick={() => { setShowMoreTerminatePackageInfoModal(false) }}>No</Button>
          </Box>
        </Modal>

        <ReassignPackageDialog
          open={showReassignDialog}
          onClose={() => setShowReassignDialog(false)}
          userId={userId}
          packageId={selectedItem?.appliedPlanId ?? ''}
          faberToken={faberToken}
        />

        <Stack>
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item>
              <NormalText isBold sx={{ fontSize: '21px' }}>
                Active/Inactive Packs
              </NormalText>
            </Grid>
          </Grid>
          <NormalText theme='muted'>
            The current status of the customer&apos;s active/unused pack(s).
          </NormalText>
        </Stack>
        <Stack flexDirection={'row'}>
          <Grid container justifyContent='space-between'>
            <Grid item xs={2}>
              <NormalText theme='muted'>Pack</NormalText>
            </Grid>
            <Grid item xs={2}>
              <NormalText theme='muted'>Data Usage</NormalText>
            </Grid>
            <Grid item xs={2}>
              <NormalText theme='muted'>Purchased</NormalText>{' '}
            </Grid>
            <Grid item xs={2}>
              <NormalText theme='muted'>Status</NormalText>{' '}
            </Grid>
            <Grid item xs={1}>
              <NormalText theme='muted'>Validity</NormalText>{' '}
            </Grid>
            <Grid item xs={1}>
              <NormalText theme='muted'>Actions</NormalText>
            </Grid>
            {getActiveOtpUserPacks}
            {getAllInactiveAndTerminatedOtpackUserPlans}
          </Grid>
        </Stack>
      </Stack >
    </>
  )
}
const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  p: 4
}
